import { useEffect, useId, useRef, useState } from 'react';
import { Pipette } from 'lucide-react';
import {
  hexToRgb,
  rgbToHex,
  hslToHex,
  contrastWithWhite,
} from '../../../../utils/palette';
import './DonutColorPicker.css';

// =============================================================
// Donut colour picker.
//
// Hue on a ring, saturation and brightness on the square inside
// it, and a strip of shades of whatever hue is picked underneath.
// The picker only ever hands back a valid six-digit hex: a half-
// typed value stays in the text box and never reaches the parent,
// so the preview beside it cannot go blank mid-keystroke.
//
// State is held as HSV rather than derived from the hex on every
// render. A hex has no hue once it is grey or black, so deriving
// it would snap the ring back to red the moment the square is
// dragged into a corner.
// =============================================================

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const wrapHue = (h) => ((h % 360) + 360) % 360;

const hsvToRgb = ({ h, s, v }) => {
  const s1 = s / 100;
  const v1 = v / 100;
  const f = (n) => {
    const k = (n + h / 60) % 6;
    return (v1 - v1 * s1 * Math.max(0, Math.min(k, 4 - k, 1))) * 255;
  };
  return { r: f(5), g: f(3), b: f(1) };
};

const rgbToHsv = ({ r, g, b }) => {
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = b / 255;
  const max = Math.max(r1, g1, b1);
  const min = Math.min(r1, g1, b1);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r1) h = ((g1 - b1) / d) % 6;
    else if (max === g1) h = (b1 - r1) / d + 2;
    else h = (r1 - g1) / d + 4;
    h = wrapHue(h * 60);
  }
  return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 };
};

const hsvToHex = (hsv) => rgbToHex(hsvToRgb(hsv));

/** Normalise any 3- or 6-digit hex to #RRGGBB, or null. */
const normaliseHex = (hex) => {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHex(rgb) : null;
};

// HSL saturation of an HSV colour, which is what the shade strip
// holds constant. Pure black and white have none, so they keep
// the HSV figure instead of collapsing the strip to grey.
const shadeSaturation = ({ s, v }) => {
  const s1 = s / 100;
  const v1 = v / 100;
  const l = v1 * (1 - s1 / 2);
  if (l <= 0 || l >= 1) return s;
  return ((v1 - l) / Math.min(l, 1 - l)) * 100;
};

// Fixed lightness steps, light to dark. Holding lightness rather
// than scaling the picked colour means the strip is the same ten
// steps for every hue, so "the 700 of this blue" and "the 700 of
// that teal" are comparable.
const SHADE_STEPS = [
  { name: '50', l: 95 },
  { name: '100', l: 89 },
  { name: '200', l: 80 },
  { name: '300', l: 70 },
  { name: '400', l: 60 },
  { name: '500', l: 50 },
  { name: '600', l: 41 },
  { name: '700', l: 33 },
  { name: '800', l: 25 },
  { name: '900', l: 17 },
];

// How far past the ring's edge a press still counts as the ring.
const RING_SLOP = 6;
const DEFAULT_RING = 24;

const FALLBACK = { h: 222, s: 75, v: 94 };

/**
 * Take a hex from outside, keeping the hue (and saturation) the
 * picker already has wherever the hex itself cannot express one.
 */
const hsvFromHex = (hex, prev) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const next = rgbToHsv(rgb);
  const hueless = next.s === 0 || next.v === 0;
  return {
    h: hueless && prev ? prev.h : next.h,
    s: next.v === 0 && prev ? prev.s : next.s,
    v: next.v,
  };
};

const DonutColorPicker = ({
  value,
  onChange,
  contrastTarget = 4.5,
  labelledBy,
}) => {
  const [hsv, setHsv] = useState(() => hsvFromHex(value) || FALLBACK);
  // The saturation the shade strip is built from. Kept apart from
  // hsv so that picking a shade does not re-derive the strip from
  // the shade's rounded hex and nudge every swatch by a digit.
  const [shadeS, setShadeS] = useState(() =>
    shadeSaturation(hsvFromHex(value) || FALLBACK),
  );
  const [draft, setDraft] = useState(value || '');

  const rootRef = useRef(null);
  const squareRef = useRef(null);
  const hueThumbRef = useRef(null);
  const svThumbRef = useRef(null);
  const dragRef = useRef(null);
  const hsvRef = useRef(hsv);
  // The last hex this picker sent up. An incoming value equal to
  // it is our own echo, not an outside change, and is ignored.
  const emittedRef = useRef(normaliseHex(value));

  const shadesId = useId();
  const canEyedrop = typeof window !== 'undefined' && 'EyeDropper' in window;

  hsvRef.current = hsv;

  // An outside change: a saved palette loaded, or a reset.
  useEffect(() => {
    const hex = normaliseHex(value);
    if (!hex) return;
    setDraft(hex);
    if (hex === emittedRef.current) return;
    emittedRef.current = hex;
    const next = hsvFromHex(hex, hsvRef.current);
    setHsv(next);
    setShadeS(shadeSaturation(next));
  }, [value]);

  const emit = (hex) => {
    emittedRef.current = hex;
    setDraft(hex);
    if (onChange) onChange(hex);
  };

  const commitHsv = (next) => {
    const clean = {
      h: wrapHue(next.h),
      s: clamp(next.s, 0, 100),
      v: clamp(next.v, 0, 100),
    };
    hsvRef.current = clean;
    setHsv(clean);
    setShadeS(shadeSaturation(clean));
    emit(hsvToHex(clean));
  };

  const commitHex = (rawHex, { fromShade = false } = {}) => {
    const hex = normaliseHex(rawHex);
    if (!hex) return;
    const prev = hsvRef.current;
    const next = hsvFromHex(hex, prev);
    // A shade is this hue by construction; keep the unrounded one.
    if (fromShade) next.h = prev.h;
    hsvRef.current = next;
    setHsv(next);
    if (!fromShade) setShadeS(shadeSaturation(next));
    emit(hex);
  };

  // ── pointer ──────────────────────────────────────────────────
  // One handler on the whole donut, and the press decides which
  // control it is for. The ring is drawn with a mask, and masks do
  // not change hit-testing, so the ring element cannot own it.

  const ringWidth = () => {
    const raw = rootRef.current
      ? getComputedStyle(rootRef.current).getPropertyValue('--dcp-ring')
      : '';
    return parseFloat(raw) || DEFAULT_RING;
  };

  const hueAt = (e) => {
    const r = rootRef.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    // 0 at twelve o'clock, clockwise -- the way the conic gradient
    // in the stylesheet is drawn.
    return wrapHue((Math.atan2(dx, -dy) * 180) / Math.PI);
  };

  const svAt = (e) => {
    const r = squareRef.current.getBoundingClientRect();
    return {
      s: clamp((e.clientX - r.left) / r.width, 0, 1) * 100,
      v: (1 - clamp((e.clientY - r.top) / r.height, 0, 1)) * 100,
    };
  };

  const track = (e) => {
    if (dragRef.current === 'hue') {
      commitHsv({ ...hsvRef.current, h: hueAt(e) });
    } else if (dragRef.current === 'sv') {
      commitHsv({ ...hsvRef.current, ...svAt(e) });
    }
  };

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const sq = squareRef.current.getBoundingClientRect();
    const inSquare =
      e.clientX >= sq.left - 4 &&
      e.clientX <= sq.right + 4 &&
      e.clientY >= sq.top - 4 &&
      e.clientY <= sq.bottom + 4;

    let mode = null;
    if (inSquare) {
      mode = 'sv';
    } else {
      const r = rootRef.current.getBoundingClientRect();
      const outer = r.width / 2;
      const d = Math.hypot(
        e.clientX - (r.left + outer),
        e.clientY - (r.top + r.height / 2),
      );
      if (d >= outer - ringWidth() - RING_SLOP && d <= outer + RING_SLOP) {
        mode = 'hue';
      }
    }
    if (!mode) return;

    e.preventDefault();
    dragRef.current = mode;
    rootRef.current.setPointerCapture(e.pointerId);
    const thumb = mode === 'hue' ? hueThumbRef.current : svThumbRef.current;
    if (thumb) thumb.focus({ preventScroll: true });
    track(e);
  };

  const onPointerMove = (e) => {
    if (dragRef.current) track(e);
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  // ── keyboard ─────────────────────────────────────────────────
  // Arrows move by one, Shift by ten. The hue wraps, because it is
  // a circle; saturation and brightness stop at their ends.

  const onHueKey = (e) => {
    const step = e.shiftKey ? 10 : 1;
    const { h } = hsvRef.current;
    let next;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = h + step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = h - step;
        break;
      case 'PageUp':
        next = h + 30;
        break;
      case 'PageDown':
        next = h - 30;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 359;
        break;
      default:
        return;
    }
    e.preventDefault();
    commitHsv({ ...hsvRef.current, h: next });
  };

  const onSvKey = (e) => {
    const step = e.shiftKey ? 10 : 1;
    const { s, v } = hsvRef.current;
    let next;
    switch (e.key) {
      case 'ArrowRight':
        next = { s: s + step, v };
        break;
      case 'ArrowLeft':
        next = { s: s - step, v };
        break;
      case 'ArrowUp':
        next = { s, v: v + step };
        break;
      case 'ArrowDown':
        next = { s, v: v - step };
        break;
      default:
        return;
    }
    e.preventDefault();
    commitHsv({ ...hsvRef.current, ...next });
  };

  // ── hex field ────────────────────────────────────────────────
  // Commits as soon as six digits are in. Three digits are only
  // expanded on blur or Enter, or typing "#3B6" on the way to
  // "#3B6FF0" would be taken as #33BB66 and rewrite the box.

  const onHexChange = (e) => {
    const digits = e.target.value
      .toUpperCase()
      .replace(/[^0-9A-F]/g, '')
      .slice(0, 6);
    setDraft(`#${digits}`);
    if (digits.length === 6) commitHex(`#${digits}`);
  };

  const settleHex = () => {
    const hex = normaliseHex(draft);
    if (!hex) {
      setDraft(normaliseHex(value) || '');
    } else if (hex === emittedRef.current) {
      // Tabbing through an untouched box. Re-committing would
      // re-derive the shade strip from the rounded hex.
      setDraft(hex);
    } else {
      commitHex(hex);
    }
  };

  const eyedrop = async () => {
    try {
      const result = await new window.EyeDropper().open();
      commitHex(result.sRGBHex);
    } catch (error) {
      // Escape closes the dropper by rejecting. Nothing to report.
    }
  };

  // ── derived ──────────────────────────────────────────────────

  const current = hsvToHex(hsv);
  const hueHex = hsvToHex({ h: hsv.h, s: 100, v: 100 });
  // 359.6 rounds to 360, which is past the slider's own maximum.
  const shadeHue = Math.round(hsv.h) % 360;
  const shades = SHADE_STEPS.map((step) => {
    const hex = hslToHex({ h: shadeHue, s: shadeS, l: step.l });
    return {
      ...step,
      hex,
      holdsWhite: contrastWithWhite(hex) >= contrastTarget,
    };
  });

  return (
    <div className="dcp" role="group" aria-labelledby={labelledBy}>
      <div
        ref={rootRef}
        className="dcp-donut"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      >
        <span className="dcp-ring" aria-hidden="true" />

        <span
          className="dcp-hue-arm"
          style={{ transform: `rotate(${hsv.h}deg)` }}
        >
          <span
            ref={hueThumbRef}
            className="dcp-hue-thumb"
            style={{ background: hueHex }}
            role="slider"
            tabIndex={0}
            aria-label="Hue"
            aria-valuemin={0}
            aria-valuemax={359}
            aria-valuenow={shadeHue}
            aria-valuetext={`${shadeHue} degrees`}
            onKeyDown={onHueKey}
          />
        </span>

        <span
          ref={squareRef}
          className="dcp-square"
          style={{ background: hueHex }}
        >
          <span
            ref={svThumbRef}
            className="dcp-sv-thumb"
            style={{
              left: `${hsv.s}%`,
              top: `${100 - hsv.v}%`,
              background: current,
            }}
            role="slider"
            tabIndex={0}
            aria-label="Saturation and brightness"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(hsv.s)}
            aria-valuetext={`Saturation ${Math.round(hsv.s)}%, brightness ${Math.round(hsv.v)}%`}
            onKeyDown={onSvKey}
          />
        </span>
      </div>

      <div className="dcp-value">
        <span
          className="dcp-swatch"
          style={{ background: current }}
          aria-hidden="true"
        />
        <input
          type="text"
          className="pg-input dcp-hex"
          value={draft}
          spellCheck="false"
          autoComplete="off"
          maxLength={7}
          onChange={onHexChange}
          onBlur={settleHex}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              settleHex();
            }
          }}
          aria-label="Hex value"
        />
        {canEyedrop && (
          <button
            type="button"
            className="dcp-eyedrop"
            onClick={eyedrop}
            title="Pick a colour from the screen"
            aria-label="Pick a colour from the screen"
          >
            <Pipette size={16} strokeWidth={2} />
          </button>
        )}
      </div>

      <div className="dcp-shades-head">
        <span className="dcp-shades-title" id={shadesId}>
          Shades of this hue
        </span>
        <span className="dcp-shades-key">
          <i aria-hidden="true">Aa</i> holds white text
        </span>
      </div>
      <div className="dcp-shades" role="group" aria-labelledby={shadesId}>
        {shades.map((shade) => {
          const isOn = shade.hex === current;
          return (
            <button
              key={shade.name}
              type="button"
              className={`dcp-shade${isOn ? ' is-on' : ''}`}
              style={{ background: shade.hex }}
              onClick={() => commitHex(shade.hex, { fromShade: true })}
              aria-pressed={isOn}
              aria-label={`Shade ${shade.name}, ${shade.hex}${shade.holdsWhite ? ', holds white text' : ''}`}
              title={`${shade.name} · ${shade.hex}`}
            >
              {shade.holdsWhite && <i aria-hidden="true">Aa</i>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DonutColorPicker;
